import { Scene } from "@babylonjs/core/scene";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3, Quaternion } from "@babylonjs/core/Maths/math.vector";
import { EventBus } from "../core/EventBus";
import { GameEvents } from "../config/events";

/** Reusable UP constant — never mutated, shared across all frames. */
const UP = new Vector3(0, 1, 0);

/** Base alpha for the stream inside the synergy radius (Q-023 tuning). */
export const STREAM_BASE_ALPHA = 0.26;

/**
 * Q-037: distance-based presence for the co-regulation stream. Full
 * presence inside the 6u synergy radius, easing linearly to 30% by the
 * 15u hide distance so the long-range stream reads as a faint tether,
 * not a laser — and no longer pops when it hides at 15u.
 */
export function computeStreamRangeFade(dist: number): number {
  if (dist <= 6.0) return 1.0;
  const t = Math.min(1.0, (dist - 6.0) / 9.0);
  return 1.0 - 0.7 * t;
}

export class ResonanceSystem {
  public isSynergyActive = false;

  private scene: Scene;
  private minnaMesh: any;
  private basilMesh: any;
  private bus: EventBus;

  // Visuals
  private streamMesh: any = null;
  private streamMaterial: PBRMaterial | null = null;

  // Telemetry
  private totalTime = 0;
  private synergyTime = 0;

  // --- Scratch Vector3 instances (pre-allocated, reused every frame) ---
  private readonly _scratchFullDir = new Vector3();
  private readonly _scratchDirUnit = new Vector3();
  private readonly _scratchAnchorA = new Vector3();
  private readonly _scratchAnchorB = new Vector3();
  private readonly _scratchA = new Vector3();
  private readonly _scratchB = new Vector3();
  private readonly _scratchDirection = new Vector3();
  private readonly _scratchMidPoint = new Vector3();
  private readonly _scratchDirNorm = new Vector3();
  private readonly _scratchAxis = new Vector3();
  // --- Scratch Quaternion (pre-allocated, mutated in-place every frame) ---
  private readonly _scratchQuat = new Quaternion();

  constructor(scene: Scene, minnaMesh: any, basilMesh: any, bus: EventBus) {
    this.scene = scene;
    this.minnaMesh = minnaMesh;
    this.basilMesh = basilMesh;
    this.bus = bus;

    this.initVisualStream();
  }

  private initVisualStream(): void {
    // Check if we are running in a mock/NullEngine environment for tests
    if (
      !this.scene ||
      typeof this.scene.getUniqueId !== "function" ||
      !this.scene.getEngine ||
      !this.scene.getEngine() ||
      this.scene.getEngine().name === "NullEngine" ||
      typeof MeshBuilder === "undefined"
    ) {
      return;
    }

    try {
      this.streamMesh = MeshBuilder.CreateCylinder(
        "resonanceStream",
        // 0.04 diameter (was 0.07): at 0.07 the stream still read as a
        // visible line in the evaluator. 0.04 makes it a subtle thread.
        { height: 1, diameter: 0.04, tessellation: 6 },
        this.scene
      );
      this.streamMesh.isPickable = false;
      this.streamMesh.receiveShadows = false;

      this.streamMaterial = new PBRMaterial("resonanceStreamMat", this.scene);
      this.streamMaterial.metallic = 0.0;
      // High roughness kills the hard specular highlight that made the
      // stream read as a sci-fi laser; we want a soft self-lit emerald
      // glow ("biodynamic flow"), not a polished rod.
      this.streamMaterial.roughness = 0.6;
      // Lower base alpha so the stream reads as luminous mist rather than
      // a solid beam. (0.42 still read as solid once the GlowLayer haloed
      // it — the stream is now also glow-excluded in setupBaseLevel.)
      this.streamMaterial.alpha = 0.06;
      this.streamMaterial.transparencyMode = PBRMaterial.MATERIAL_ALPHABLEND;

      // Default to glowing emerald green — dimmed so it reads as hint, not beam
      this.streamMaterial.albedoColor = new Color3(0.0, 0.4, 0.15);
      this.streamMaterial.emissiveColor = new Color3(0.0, 0.18, 0.08);

      this.streamMesh.material = this.streamMaterial;
      // Assign the scratch Quaternion as the mesh's rotationQuaternion once;
      // subsequent frames mutate it in-place via RotationAxisToRef — no alloc.
      this.streamMesh.rotationQuaternion = this._scratchQuat;
    } catch (e) {
      console.warn("[ResonanceSystem] Visual stream failed to initialize:", e);
    }
  }

  public update(dt: number): void {
    if (!this.minnaMesh || !this.basilMesh) return;

    const posMinna = this.minnaMesh.position;
    const posBasil = this.basilMesh.position;

    // Calculate distance on 2D plane (Z = 0)
    const dx = posMinna.x - posBasil.x;
    const dy = posMinna.y - posBasil.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    this.totalTime += dt;
    if (dist <= 6.0) {
      this.synergyTime += dt;
      if (!this.isSynergyActive) {
        this.isSynergyActive = true;
        this.bus.emit(GameEvents.SYNERGY_ENTERED);
      }
    } else if (dist > 9.0) {
      if (this.isSynergyActive) {
        this.isSynergyActive = false;
        this.bus.emit(GameEvents.SYNERGY_EXITED);
      }
    }

    // Update visuals if available
    this.updateVisuals(posMinna, posBasil, dist);
  }

  private updateVisuals(posA: Vector3, posB: Vector3, dist: number): void {
    if (!this.streamMesh || !this.streamMaterial) return;

    if (dist > 15.0) {
      // Hide if extremely far apart
      this.streamMesh.setEnabled(false);
      return;
    }

    this.streamMesh.setEnabled(true);

    // Inset the stream tips back toward each cat's surface so the flow
    // reads as connecting them rather than spearing through their bodies.
    // Clamp the inset for short separations so it never inverts.
    // All intermediates use pre-allocated scratch Vector3 — no per-frame heap allocation.
    //
    // Anchor the flow at chest height, not the capsule center: the
    // center sits at the cats' head/eye line, and a straight emissive
    // rod at eye height reads as an eye-laser (Q-023). Copy into
    // dedicated anchor scratch — posA/posB are the live physics
    // positions and must not be mutated, and _scratchA/_scratchB are
    // reused as intermediates below.
    this._scratchAnchorA.copyFrom(posA);
    this._scratchAnchorA.y -= 0.35;
    posA = this._scratchAnchorA;
    this._scratchAnchorB.copyFrom(posB);
    this._scratchAnchorB.y -= 0.35;
    posB = this._scratchAnchorB;
    posB.subtractToRef(posA, this._scratchFullDir);
    const fullLen = this._scratchFullDir.length();
    if (fullLen > 1e-4) {
      this._scratchFullDir.scaleToRef(1 / fullLen, this._scratchDirUnit);
    } else {
      this._scratchDirUnit.copyFrom(UP);
    }
    const inset = Math.min(0.45, fullLen * 0.3);

    // a = posA + dirUnit * inset
    this._scratchDirUnit.scaleToRef(inset, this._scratchA);
    this._scratchA.addInPlace(posA);

    // b = posB - dirUnit * inset
    this._scratchDirUnit.scaleToRef(inset, this._scratchB);
    // safe self-alias: subtractToRef reads both operands before writing the
    // result, so result === other (_scratchB) computes posB - _scratchB
    // correctly. Do NOT "fix" by adding a temp — it's intentional pooling.
    posB.subtractToRef(this._scratchB, this._scratchB);

    // direction = b - a
    this._scratchB.subtractToRef(this._scratchA, this._scratchDirection);

    // midPoint = a + direction * 0.5
    this._scratchDirection.scaleToRef(0.5, this._scratchMidPoint);
    this._scratchMidPoint.addInPlace(this._scratchA);

    // Position at midpoint
    this.streamMesh.position.copyFrom(this._scratchMidPoint);

    // Scale Y (height); breathe the diameter gently so the flow feels
    // alive rather than rigid. Frame-rate independent — driven by the
    // accumulated totalTime, not per-frame increments.
    const height = this._scratchDirection.length();
    const rangeFade = computeStreamRangeFade(dist);
    // Taper girth with distance on top of the breathe so the far stream thins.
    const breathe = (1 + 0.18 * Math.sin(this.totalTime * 3.2)) * (0.65 + 0.35 * rangeFade);
    this.streamMesh.scaling.set(breathe, height, breathe);

    // Rotate cylinder to align along direction vector
    // Normalize in place into scratch — no new Vector3 allocated.
    this._scratchDirection.normalizeToRef(this._scratchDirNorm);

    Vector3.CrossToRef(UP, this._scratchDirNorm, this._scratchAxis);
    const dot = Vector3.Dot(UP, this._scratchDirNorm);
    const angle = Math.acos(Math.max(-1, Math.min(1, dot)));

    if (this._scratchAxis.lengthSquared() > 0.0001) {
      this._scratchAxis.normalize();
      // RotationAxisToRef mutates _scratchQuat in place — no per-frame alloc.
      // _scratchQuat is already assigned as streamMesh.rotationQuaternion (once,
      // in initVisualStream), so the mesh picks up the mutation automatically.
      Quaternion.RotationAxisToRef(this._scratchAxis, angle, this._scratchQuat);
    } else {
      // Degenerate case (UP ∥ direction): set to identity in place.
      this._scratchQuat.set(0, 0, 0, 1);
    }

    // Dynamic color shifting based on distance:
    // Green (0 -> 6.0) -> Yellow/Orange (6.0 -> 9.0) -> Red/Rust (9.0 -> 15.0)
    if (dist <= 6.0) {
      this.streamMaterial.albedoColor.set(0.0, 0.9, 0.4);
      this.streamMaterial.emissiveColor.set(0.0, 0.8, 0.35);
    } else if (dist <= 9.0) {
      const t = (dist - 6.0) / 3.0; // [0..1]
      // Lerp Green to Yellow
      this.streamMaterial.albedoColor.set(t * 0.9, 0.9 - t * 0.1, 0.4 - t * 0.4);
      this.streamMaterial.emissiveColor.set(t * 0.8, 0.8 - t * 0.1, 0.35 - t * 0.35);
    } else {
      const t = Math.min(1.0, (dist - 9.0) / 6.0); // [0..1]
      // Lerp Yellow to Rust Red
      this.streamMaterial.albedoColor.set(0.9 - t * 0.3, 0.8 - t * 0.6, 0.0);
      this.streamMaterial.emissiveColor.set(0.8 - t * 0.3, 0.7 - t * 0.6, 0.0);
    }

    // Gentle luminous pulse on top of the band color, so the emerald
    // flow glows like something breathing rather than a steady beam.
    const pulse = 0.82 + 0.18 * Math.sin(this.totalTime * 4.0);
    // rangeFade also dims emissive at distance (reads as desaturation while
    // the albedo band color keeps signalling the synergy state).
    this.streamMaterial.emissiveColor.scaleInPlace(pulse * rangeFade);
    this.streamMaterial.alpha = STREAM_BASE_ALPHA * rangeFade;
  }

  public getSynergyTimeRatio(): number {
    return this.totalTime > 0 ? this.synergyTime / this.totalTime : 0.0;
  }

  /**
   * The stream cylinder, for glow-layer exclusion at level setup.
   * Null in NullEngine/test environments (initVisualStream bails).
   */
  public getStreamMesh(): unknown {
    return this.streamMesh;
  }

  public dispose(): void {
    if (this.streamMesh) {
      this.streamMesh.dispose();
      this.streamMesh = null;
    }
    if (this.streamMaterial) {
      this.streamMaterial.dispose();
      this.streamMaterial = null;
    }
  }
}
